/*
* (C) Behaviour Interactive Inc. - All Rights Reserved
* Unauthorized copying of this file, via any medium, is strictly prohibited
* This file is proprietary and confidential
*/

import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Sync } from '@mui/icons-material';
import { GameIds } from '../../account/my-games/MyGames.model';
import { MergedAccount } from '../../authentication/Authentication.model';
import { GameConfig } from '../../Config.model';
import { useConfig } from '../../ConfigContext';
import { RequestError } from '../../Error.model';
import { useSnackbar } from '../../snackbar/SnackbarContext';
import { get } from '../../utils/Request';
import { Tooltip } from '../tooltip/Tooltip';
import { getProviderLogo } from '../../account/providers/ProviderLogo';
import { useAuth } from '../../authentication/AuthenticationContext';
import { ProviderMergeList } from '../../account/providers/provider/ProviderMergeList';

export default function CrossProgression(): JSX.Element {
  const { t } = useTranslation(['dbd', 'translation']);
  const config = useConfig();
  const snackbar = useSnackbar();
  const history = useHistory();
  const auth = useAuth();
  const { gameId }: { gameId: GameIds } = useParams();

  const [gameConfig, setGameConfig] = useState<GameConfig>(null);
  const [mergedAccounts, setMergedAccounts] = useState<MergedAccount[]>([]);
  const [showMergeCards, setShowMergeCards] = useState(false);

  const availableProviders = gameConfig?.providers.filter((provider) => provider.isMergeImplemented);

  const getMergedAccounts = async (): Promise<void> => {
    try {
      setMergedAccounts(await get<MergedAccount[]>(
        `${config.clientConfig.host}/players/me/mergedAccounts/${gameId}`,
      ));
    } catch (e) {
      const err = e as RequestError;
      snackbar.addErrorMessage(err.code);
    }
  };

  useEffect(() => {
    const co = config.serverConfig.gamesConfig?.find(((gc) => gc.id === gameId));
    if (co) {
      setGameConfig(co);
      getMergedAccounts();
      setShowMergeCards(auth.user.accounts.some((acc) => co.providers.some((x) => x.name === acc.type)));
    }
  }, []);

  return (availableProviders?.length > 0
    && (
      <section className='cross-progression-component'>
        <div className='subtitle'>
          <h3 className='cross-progression-title'>
            {t('translation:account.my-account.crossProgression')}
          </h3>
          <Tooltip id='merge' message='tooltip.merge' />
        </div>

        {showMergeCards && (
          <ProviderMergeList
            providers={availableProviders}
            gameId={gameId}
            mergedAccounts={mergedAccounts}
            getMergedAccounts={getMergedAccounts}
          />
        )}

        {!showMergeCards && (
          <div className='provider-card provider-merge-unlock text-center'>
            <div className='providers-merge-sync'>
              {availableProviders?.map((provider, i) => (
                <Fragment key={`provider-icon-${provider.name}`}>
                  {i !== 0 && (<Sync style={{ fontSize: '2rem' }} />)}
                  <img
                    src={getProviderLogo(provider.name)}
                    alt={provider.name}
                    height='48'
                  />
                </Fragment>
              ))}
            </div>
            <h3>{t('translation:account.provider.unlock-merge-feature')}</h3>
            <button
              type='button'
              onClick={() => history.push('/account/my-account/providers')}
              className='btn btn-primary-game mx-auto mt-3 mb-0'
              data-cy='games-sync-button'
            >
              {t('translation:account.game-library.sync-gaming-accounts')}
            </button>
          </div>
        )}
      </section>
    )
  );
}
